import type { MapObjectReference } from '../../../map/core/map-object';
import type { MapObjectPopover } from '../../../map/core/map-object-popover';
import { LeadProfile } from '../../../services/lead-api.service';
import { LeadAssigneeLookup } from '../../../services/lookup-api.service';
import { businessTypeLabels } from './business-types';

/** One line of the record: what it is called, and what it says. */
export interface LeadRecordRow {
  label: string;
  value: string;
  /** True when the lead simply has no answer here. Rendered as a dash, and
   *  greyed, so an empty field reads as unanswered rather than as a value. */
  empty: boolean;
  /** Structured location for the shared pin; never parse the displayed value. */
  location?: {
    latitude: number | null;
    longitude: number | null;
    object: MapObjectReference;
    popup: MapObjectPopover;
  };
}

export interface LeadRecordSection {
  title: string;
  rows: LeadRecordRow[];
}

/** How far off the next action is, and therefore what the page should say. */
export type NextActionState = 'none' | 'overdue' | 'today' | 'upcoming';

export interface NextAction {
  state: NextActionState;
  /** The verb somebody typed — free text, and often absent even when a date
   *  is set. */
  type: string | null;
  due: string | null;
  /** Whole days between today and the due date; negative once it is late. */
  days: number | null;
}

const ORIGIN_LABELS: Record<string, string> = {
  manual: 'Manual',
  inbound: 'Inbound',
  firstparty: 'First party',
  import: 'Import',
  discovery: 'Discovery',
};

/** Local midnight, so a due date compares against the day the user is having
 *  rather than against an instant in UTC. The board's own overdue test does the
 *  same comparison the same way. */
function startOfToday(now: Date): Date {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

/** A yyyy-MM-dd date, read as a local date. `new Date('2026-08-13')` is parsed
 *  as UTC midnight, which lands on the previous day west of Greenwich — the
 *  difference between "due today" and "a day late". */
function localDate(value: string): Date | null {
  const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!parts) {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
}

/**
 * What the lead is waiting for.
 *
 * The record carries a free-text `nextActionType` and a `nextActionDue` date,
 * either of which may be missing. A page that printed them raw would say
 * "Call · 2026-08-01" and leave the reader to work out that it is nine days
 * late; the state below is the part worth saying out loud.
 */
export function nextActionOf(profile: LeadProfile, now: Date = new Date()): NextAction {
  const type = profile.nextActionType?.trim() || null;
  const due = profile.nextActionDue?.trim() || null;
  if (!due) return { state: 'none', type, due: null, days: null };

  const dueDate = localDate(due);
  if (!dueDate) return { state: 'none', type, due: null, days: null };

  const dayMs = 24 * 60 * 60 * 1000;
  // Floor, not round: a due date is a DAY, so anything at all before today
  // is late. Rounding would call a date half a day behind "today" — which
  // is exactly what a UTC-parsed date looks like from a western timezone,
  // and it would hide the bug localDate() above exists to prevent.
  const days = Math.floor((dueDate.getTime() - startOfToday(now).getTime()) / dayMs);
  const state: NextActionState = days < 0 ? 'overdue' : days === 0 ? 'today' : 'upcoming';
  return { state, type, due, days };
}

/** The sentence the page leads with. */
export function nextActionHeadline(action: NextAction): string {
  const what = action.type ?? 'Follow up';
  switch (action.state) {
    case 'none':
      return action.type
        ? `${what} — no date set`
        : 'Nothing scheduled';
    case 'overdue': {
      const late = Math.abs(action.days ?? 0);
      return `${what} — ${late} ${late === 1 ? 'day' : 'days'} overdue`;
    }
    case 'today':
      return `${what} — due today`;
    default: {
      const days = action.days ?? 0;
      return `${what} — due in ${days} ${days === 1 ? 'day' : 'days'}`;
    }
  }
}

function money(value: number | null): string {
  return value == null ? '' : value.toLocaleString(undefined, {
    style: 'currency', currency: 'USD', maximumFractionDigits: 0,
  });
}

function day(value: string | null): string {
  if (!value) return '';
  const parsed = localDate(value);
  return parsed ? parsed.toLocaleDateString() : value;
}

function moment(value: string | null): string {
  if (!value) return '';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

function row(label: string, value: string | number | null | undefined): LeadRecordRow {
  const text = value == null || value === '' ? '' : String(value);
  return { label, value: text || '—', empty: !text };
}

/**
 * Every field the lead profile carries, in the order a person reads a record:
 * who it is, where it is, what it is worth, and when it was last touched.
 *
 * Built here rather than in the template so the set is one list that a test can
 * count — the view used to be the edit form with its controls disabled, which
 * silently dropped the four fields the form has no input for.
 */
export function leadRecord(
  profile: LeadProfile,
  reps: LeadAssigneeLookup[],
): LeadRecordSection[] {
  const owner = reps.find(rep => rep.id === profile.salesRepUserId);
  const salesRepName = owner
    ? owner.displayName?.trim() || owner.userName?.trim() || `User ${owner.id}`
    : profile.salesRepUserId != null ? `User ${profile.salesRepUserId}` : '';

  return [
    {
      title: 'Lead',
      rows: [
        row('Name', profile.name),
        row('Stage', profile.leadStageName),
        row('Sales rep', salesRepName),
        // Labelled and joined: the record prints what the lead is, not the
        // keys the classifier writes it with.
        row('Business types', businessTypeLabels(
          profile.businessTypes.map(type => type.name)).join(', ')),
        row('Origin', ORIGIN_LABELS[profile.origin] ?? profile.origin),
      ],
    },
    {
      title: 'Address',
      rows: [
        row('Address line 1', profile.addressLine1),
        row('City', profile.city),
        row('State', profile.state),
        row('ZIP / Postal', profile.postalCode),
        {
          ...row('Coordinates', profile.latitude != null && profile.longitude != null
            ? `${profile.latitude}, ${profile.longitude}` : ''),
          location: {
            latitude: profile.latitude,
            longitude: profile.longitude,
            object: { featureKey: 'leads', objectId: profile.id },
            popup: {
              type: 'Lead',
              title: profile.name || ('Lead #' + profile.id),
              subtitle: profile.addressLine1 || undefined,
            },
          },
        },
      ],
    },
    // Two boxes, not one called Pipeline. Those were different questions
    // under one heading - what the deal is worth, and what happens next -
    // and "Pipeline" named the system rather than either of them. It also
    // outlived the module: this is called Leads everywhere now.
    {
      title: 'Value',
      rows: [
        row('Probability', profile.probability == null
          ? '' : `${Math.round(profile.probability * 100)}%`),
        row('Annual revenue', money(profile.expectedAnnualRevenue)),
        row('Annual contribution', money(profile.expectedAnnualContribution)),
      ],
    },
    {
      title: 'Next step',
      rows: [
        row('Next action', profile.nextActionType),
        row('Next action due', day(profile.nextActionDue)),
      ],
    },
    {
      title: 'Record',
      rows: [
        row('Lead id', profile.id),
        row('Created', moment(profile.createdAt)),
        row('Last updated', moment(profile.updatedAt)),
      ],
    },
  ];
}
